import {
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	Button,
	Input,
	Textarea,
	Badge,
	Switch,
} from "@/components/primitives";
import {
	ArrowRight,
	Edit,
	Plus,
	RefreshCw,
	Loader2,
	Activity,
} from "lucide-react";

export interface Signal {
	id: string;
	title: string;
	description: string;
	selected: boolean;
	isEditing?: boolean;
}

interface SignalsStepProps {
	/**
	 * List of signals
	 */
	signals: Signal[];
	/**
	 * Whether signals are being loaded
	 */
	isLoading: boolean;
	/**
	 * Error message, if any
	 */
	error: string | null;
	/**
	 * Callback when a signal is updated
	 */
	onUpdateSignal: (id: string, field: string, value: any) => void;
	/**
	 * Callback when a signal is toggled
	 */
	onToggleSignal: (id: string) => void;
	/**
	 * Callback to generate more suggestions
	 */
	onGenerateMore: () => void;
	/**
	 * Callback to add new signal
	 */
	onAddNew: () => void;
	/**
	 * Callback when user clicks "Done"
	 */
	onContinue: () => void;
}

/**
 * Helper to count selected signals
 */
function getSelectedCount(signals: Signal[]): number {
	return signals.filter((s) => s.selected).length;
}

export function SignalsStep({
	signals,
	isLoading,
	error,
	onUpdateSignal,
	onToggleSignal,
	onGenerateMore,
	onAddNew,
	onContinue,
}: SignalsStepProps) {
	const selectedCount = getSelectedCount(signals);
	const maxSelected = 3;
	const isDisabled = selectedCount === 0;

	return (
		<>
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600">
						<Activity className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Your suggested signals
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							Press the toggle switch to select up to {maxSelected} signals
							which you relate to most
							<br />
							(feel free to edit or create your own)
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-4">
					{/* Error Alert */}
					{error && (
						<div className="border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/20">
							<p className="text-sm text-red-800 dark:text-red-200">{error}</p>
						</div>
					)}

					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-6 w-6 animate-spin mr-2 text-primary" />
							<span className="text-muted-foreground">
								Generating signals...
							</span>
						</div>
					) : (
						<>
							{/* Signals List */}
							{signals.map((signal) => (
								<div
									key={signal.id}
									className={`border rounded-lg p-3 sm:p-4 ${signal.selected ? "border-green-500" : "border-border"}`}
								>
									{/* Header Row: Title and Actions */}
									<div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-2">
										<div className="flex-1 mb-3 sm:mb-0">
											{signal.isEditing ? (
												<Input
													value={signal.title}
													onChange={(e) =>
														onUpdateSignal(signal.id, "title", e.target.value)
													}
													placeholder="Signal title"
													className="mb-2"
												/>
											) : (
												<h4 className="font-medium text-base sm:text-sm">
													{signal.title}
												</h4>
											)}
										</div>

										{/* Edit and Select Buttons */}
										<div className="flex items-center justify-end gap-3 sm:gap-2 sm:ml-4">
											<Button
												variant="ghost"
												size="sm"
												onClick={() =>
													onUpdateSignal(
														signal.id,
														"isEditing",
														!signal.isEditing,
													)
												}
												className="h-8 w-8 sm:h-auto sm:w-auto p-2 sm:p-1"
												title={
													signal.isEditing ? "Done editing" : "Edit signal"
												}
											>
												<Edit className="h-4 w-4" />
											</Button>
											<Switch
												checked={signal.selected}
												onCheckedChange={() => onToggleSignal(signal.id)}
												disabled={
													!signal.selected && selectedCount >= maxSelected
												}
												className="scale-110 sm:scale-100"
											/>
										</div>
									</div>

									{/* Description */}
									{signal.isEditing ? (
										<Textarea
											value={signal.description}
											onChange={(e) =>
												onUpdateSignal(signal.id, "description", e.target.value)
											}
											placeholder="Signal description"
											className="min-h-[80px]"
										/>
									) : (
										<p className="text-sm text-muted-foreground leading-relaxed">
											{signal.description}
										</p>
									)}
								</div>
							))}

							{/* Action Buttons */}
							<div className="flex flex-col sm:flex-row gap-3 sm:gap-2 pt-2">
								<Button
									variant="outline"
									onClick={onGenerateMore}
									disabled={isLoading}
									className="w-full sm:w-auto"
								>
									<RefreshCw className="h-4 w-4 mr-2" />
									Generate more suggestions
								</Button>
								<Button
									variant="outline"
									onClick={onAddNew}
									className="w-full sm:w-auto"
								>
									<Plus className="h-4 w-4 mr-2" />
									Add new
								</Button>
							</div>

							{/* Selection Count and Continue Button */}
							<div className="flex flex-col sm:flex-row items-center justify-between pt-4 gap-3 sm:gap-0">
								<Badge variant="outline" className="text-center">
									{selectedCount} of {maxSelected} selected
								</Badge>
								<Button
									onClick={onContinue}
									disabled={isDisabled}
									className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50"
								>
									Done
									<ArrowRight className="h-4 w-4 ml-2" />
								</Button>
							</div>
						</>
					)}
				</div>
			</CardContent>
		</>
	);
}
